import { hydraUiApi, useHydraUIAPIQuery } from '@/services/api';
import { useAuth } from '@/services/auth';
import { isBuilderPlan } from '@/services/auth/plan-slugs';
import { hasGrantStarted, spendableGrantOriginalTotal, spendableGrantTotal } from '@/services/billing/credit-grants';
import { dollarsToCents } from '@/utils/currency';

/**
 * Fetches the builder credit balance for the current account.
 * `credits_remaining` values are dollar amounts (e.g. 42.57 = $42.57).
 *
 * Reports `totalCents` (funds loaded) and `usedCents` (funds spent) beside the remaining balance, so
 * a usage meter fills from the same grants the balance reads.
 */
export function useBuilderBalance() {
  const { account } = useAuth();
  const isBuilder = isBuilderPlan(account);

  const { data, isLoading, isError } = useHydraUIAPIQuery({
    queryKey: ['getBuilderCredits', { params: { account_id: account.id } }],
    queryFn: ({ signal }) =>
      hydraUiApi.accounts.getBuilderCredits({
        params: { path: { account_id: String(account.id) } },
        signal,
      }),
    queryConfig: { enabled: isBuilder },
  });

  // Scheduled grants (`starts_at` in the future) are not spendable yet, so they must not count
  // toward the balance that feeds the $1,000 top-up cap — counting them blocks a purchase the
  // account is entitled to make.
  const nowSec = Math.floor(Date.now() / 1000);
  const grants = isBuilder ? (data?.grants ?? []) : [];
  const balanceCents = dollarsToCents(spendableGrantTotal(grants, nowSec));
  const totalCents = dollarsToCents(spendableGrantOriginalTotal(grants, nowSec));
  // Clamp: a remaining balance above its original (never expected) must not report negative use.
  const usedCents = Math.max(0, totalCents - balanceCents);

  // Only paid grants are top-ups. Expired top-ups stay in, so an account with no funds left is still funded.
  const topUps = grants.filter((grant) => grant.tier === 'paid' && hasGrantStarted(grant, nowSec));
  const hasFunded = topUps.length > 0;

  return {
    balanceCents,
    totalCents,
    usedCents,
    hasFunded,
    isBuilder,
    isLoading,
    isError,
  };
}
